import { useEffect, useRef } from "react";

/**
 * Loads whatever measurement tags are configured in the admin panel.
 *
 * Nothing is fetched and no cookie is set until an id is filled in, so a site
 * with the fields empty makes no third-party request at all.
 *
 * The ids are validated server-side against the shape each vendor issues, so
 * what lands in the script URLs below cannot be anything else. `custom_head`
 * is not validated and cannot be — it is there for the tag that does not fit,
 * and whatever it contains runs on every page.
 */

const injected = new Set();

/** Appends a node once, keyed so a re-render cannot load a tag twice. */
const once = (key, build) => {
  if (injected.has(key)) return;
  injected.add(key);
  build();
};

const script = (attrs, code) => {
  const el = document.createElement("script");
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  if (code) el.text = code;
  document.head.appendChild(el);
};

const loadGoogle = (id) => {
  script({ async: "", src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}` });
  script({}, `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}`
    + `gtag('js',new Date());gtag('config',${JSON.stringify(id)});`);
};

const loadTagManager = (id) => {
  script({}, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});`
    + `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';`
    + `j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);`
    + `})(window,document,'script','dataLayer',${JSON.stringify(id)});`);
};

const loadMetaPixel = (id) => {
  script({}, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?`
    + `n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;`
    + `n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;`
    + `t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}`
    + `(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');`
    + `fbq('init',${JSON.stringify(id)});fbq('track','PageView');`);
};

/**
 * Runs the markup from the free-form field.
 *
 * Assigning it to innerHTML would insert the tags but never execute them —
 * the browser refuses to run a script that arrived that way — so each script
 * element is rebuilt. Everything else is moved across as it is.
 */
const loadCustom = (html) => {
  const parsed = new DOMParser().parseFromString(`<head>${html}</head>`, "text/html");
  [...parsed.head.childNodes].forEach((node) => {
    if (node.nodeName === "SCRIPT") {
      const el = document.createElement("script");
      [...node.attributes].forEach((a) => el.setAttribute(a.name, a.value));
      el.text = node.textContent;
      document.head.appendChild(el);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      document.head.appendChild(node.cloneNode(true));
    }
  });
};

export const useAnalytics = (analytics) => {
  // Settings arrive after the first render and refresh when they are saved;
  // this keeps a tag from being loaded again each time that happens.
  const seen = useRef(false);

  useEffect(() => {
    if (!analytics || seen.current) return;
    const { ga4_id: ga4, gtm_id: gtm, meta_pixel_id: pixel, custom_head: custom } = analytics;
    if (!ga4 && !gtm && !pixel && !custom) return;
    seen.current = true;

    if (ga4) once("ga4", () => loadGoogle(ga4));
    if (gtm) once("gtm", () => loadTagManager(gtm));
    if (pixel) once("pixel", () => loadMetaPixel(pixel));
    if (custom?.trim()) once("custom", () => loadCustom(custom));
  }, [analytics]);
};
